import React from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from '@headlessui/react';
import useFiles from '../../../hooks/useFiles';
import Pagination from '../../Utils/Pagination/Pagination';

interface IFile {
  dddfile: string;
  user: number;
  upload_date: string;
  index?: number;
}

interface FilesDialogProps {
  dialogOpen: boolean;
  setDialogOpen: (value: boolean) => void;
  email: string;
}

const dateFormatter = new Intl.DateTimeFormat('pl-PL', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

const FilesDialog: React.FC<FilesDialogProps> = ({
  dialogOpen,
  setDialogOpen,
  email,
}) => {
  const {
    files,
    error,
    loading,
    next,
    previous,
    goToNextPage,
    goToPreviousPage,
    currentPage,
  } = useFiles(email, dialogOpen);

  return (
    <Dialog open={dialogOpen} onClose={setDialogOpen} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-slate-900/30 backdrop-blur-[2px] transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in dark:bg-navy-900/55"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full w-full items-center justify-center p-4 sm:p-6">
          <DialogPanel
            transition
            className="relative w-full max-w-3xl transform overflow-hidden rounded-2xl border border-white/70 bg-white/95 text-left shadow-[0_30px_80px_rgba(15,23,42,0.22)] ring-1 ring-slate-200/70 backdrop-blur transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95 dark:border-navy-500 dark:bg-navy-800/95 dark:ring-navy-400/30">
            <div className="border-b border-slate-200/80 bg-gradient-to-r from-white via-slate-50 to-indigo-50 px-5 py-5 dark:border-navy-500 dark:from-navy-800 dark:via-navy-750 dark:to-navy-700 sm:px-7">
              <DialogTitle
                as="h3"
                className="text-3xl font-semibold tracking-tight text-slate-700 dark:text-navy-50">
                Pliki DDD
              </DialogTitle>
              <p className="mt-1 text-sm text-slate-500 dark:text-navy-200">
                Pobierz pliki przypisane do wybranego użytkownika.
              </p>
            </div>

            <div className="max-h-[60vh] overflow-y-auto px-5 py-5 sm:px-7">
              {error ? (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-5 text-base text-red-600 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
                  {error}
                </div>
              ) : loading ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-base text-slate-600 dark:border-navy-500 dark:bg-navy-700/60 dark:text-navy-100">
                  Ładowanie plików...
                </div>
              ) : files.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-5 text-base text-slate-600 dark:border-navy-500 dark:bg-navy-700/60 dark:text-navy-100">
                  Brak plików
                </div>
              ) : (
                <div className="space-y-3">
                  {files.map((file: IFile, index: number) => (
                    <div
                      key={index}
                      className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-slate-50/85 p-4 shadow-sm transition-colors hover:border-slate-300 hover:bg-white dark:border-navy-500 dark:bg-navy-700/50 dark:hover:bg-navy-700 sm:flex-row sm:items-center sm:justify-between">
                      <div className="flex items-center gap-3">
                        <span className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary dark:bg-accent/15 dark:text-accent">
                          <i className="fa-solid fa-file-arrow-down text-sm"></i>
                        </span>
                        <div>
                          <p className="text-base font-medium text-slate-700 dark:text-navy-50">
                            Plik DDD #{file.index}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-navy-200">
                            {dateFormatter.format(new Date(file.upload_date))}
                          </p>
                        </div>
                      </div>

                      <a
                        href={file.dddfile}
                        download
                        className="btn h-11 rounded-xl bg-primary px-4 text-white font-medium shadow-sm hover:bg-primary-focus focus:bg-primary-focus active:bg-primary-focus/90 dark:bg-accent dark:hover:bg-accent-focus dark:focus:bg-accent-focus dark:active:bg-accent/90">
                        Pobierz
                      </a>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200/80 bg-white/90 px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7 dark:border-navy-500 dark:bg-navy-800/90">
              <Pagination
                currentPage={currentPage}
                goToPreviousPage={goToPreviousPage}
                goToNextPage={goToNextPage}
                prvPage={previous}
                nextPage={next}
              />
              <button
                type="button"
                data-autofocus
                onClick={() => setDialogOpen(false)}
                className="btn h-11 rounded-xl bg-primary px-5 font-medium text-white hover:bg-primary-focus focus:bg-primary-focus active:bg-primary-focus/90 dark:bg-accent dark:hover:bg-accent-focus dark:focus:bg-accent-focus dark:active:bg-accent/90">
                Zamknij
              </button>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

export default FilesDialog;
